import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { useHandHistory } from 'hooks/useHandHistory';

import {
  handHistoryScenarioLabels,
  handHistoryScenarios
} from './__fixtures__/handHistory.fixtures';
import { PREVIEW_PLAYER_COUNTS } from './__fixtures__/previewPlayerCount';
import { useDemoLiveHand, useDemoPlayerCount } from './HandHistoryDemoProvider';

import styles from './HandHistory.module.scss';

export const HandHistoryDemoControls = () => {
  const [isOpen, setIsControlsOpen] = useState(false);
  const {
    demoLocked,
    setDemoLocked,
    demoGameMode,
    setDemoGameMode,
    demoVariant,
    setDemoVariant,
    setIsOpen,
    data,
    selectedIndex,
    setSelectedIndex
  } = useHandHistory();
  const { demoPlayerCount, setDemoPlayerCount, layout } = useDemoPlayerCount();
  const { livePlaying, liveDone, setLivePlaying, restartLiveHand } = useDemoLiveHand();
  const isOmaha = demoVariant === 'plo' || demoVariant === 'plo8';
  const original = handHistoryScenarios[isOmaha ? 'cash' : demoGameMode][demoVariant];
  const selectedHandId = data.completedHands[selectedIndex]?.handId;
  const minimumPlayers = (
    original.completedHands.find((hand) => hand.handId === selectedHandId) ??
    original.completedHands[0]
  ).tableSnapshot.seats.length;

  return (
    <div className={styles.demoControls}>
      <button
        type='button'
        className={styles.demoControlsToggle}
        aria-expanded={isOpen}
        onClick={() => setIsControlsOpen((value) => !value)}>
        <span className={styles.demoControlsToggleIcon} aria-hidden='true'>
          {isOpen ? <CloseIcon /> : <MenuIcon />}
        </span>
        {isOpen ? 'Controls' : 'Controls'}
      </button>

      {isOpen && (
        <div className={styles.demoControlsPanel}>
          <p className={styles.demoNote}>
            Sample hands · no live game data. The table shows the selected completed example.
            {layout === 'current'
              ? ' Expand a hand card to browse its details.'
              : layout === 'v1'
              ? ' Use Active for the current hand or Completed to browse concluded hands.'
              : ' Expand or select a hand to browse its details.'}
          </p>
          <div className={styles.demoControlGroup}>
            <button
              type='button'
              className={demoLocked ? styles.demoControlActive : ''}
              aria-pressed={demoLocked}
              onClick={() => {
                setDemoLocked(true);
                setIsOpen(true);
              }}>
              Not dealt in
            </button>
            <button
              type='button'
              className={!demoLocked ? styles.demoControlActive : ''}
              aria-pressed={!demoLocked}
              onClick={() => {
                setDemoLocked(false);
                setIsOpen(true);
              }}>
              Dealt in
            </button>
          </div>
          <div className={styles.demoControlGroup}>
            <button
              type='button'
              className={demoGameMode === 'cash' ? styles.demoControlActive : ''}
              aria-pressed={demoGameMode === 'cash'}
              onClick={() => setDemoGameMode('cash')}>
              Cash game
            </button>
            <button
              type='button'
              className={demoGameMode === 'tournament' && !isOmaha ? styles.demoControlActive : ''}
              aria-pressed={demoGameMode === 'tournament' && !isOmaha}
              disabled={isOmaha}
              onClick={() => setDemoGameMode('tournament')}>
              Tournament
            </button>
          </div>
          <div className={styles.demoControlGroup}>
            <button
              type='button'
              className={demoVariant === 'texas' ? styles.demoControlActive : ''}
              aria-pressed={demoVariant === 'texas'}
              onClick={() => setDemoVariant('texas')}>
              Hold&apos;em
            </button>
            <button
              type='button'
              className={demoVariant === 'plo' ? styles.demoControlActive : ''}
              aria-pressed={demoVariant === 'plo'}
              onClick={() => {
                setDemoGameMode('cash');
                setDemoVariant('plo');
              }}>
              PLO
            </button>
            <button
              type='button'
              className={demoVariant === 'plo8' ? styles.demoControlActive : ''}
              aria-pressed={demoVariant === 'plo8'}
              onClick={() => {
                setDemoGameMode('cash');
                setDemoVariant('plo8');
              }}>
              PLO8
            </button>
          </div>
          <label className={styles.demoScenario}>
            Number of players
            <select
              value={Math.max(minimumPlayers, demoPlayerCount ?? minimumPlayers)}
              onChange={(event) => setDemoPlayerCount(Number(event.target.value))}>
              {PREVIEW_PLAYER_COUNTS.map((count) => (
                <option key={count} value={count} disabled={count < minimumPlayers}>
                  {count} players
                </option>
              ))}
            </select>
          </label>
          {minimumPlayers > 2 && (
            <p className={styles.demoNote}>This example needs at least {minimumPlayers} players.</p>
          )}
          {!demoLocked && (
            <>
              <div className={styles.demoControlGroup} role='group' aria-label='Live hand'>
                <button
                  type='button'
                  className={livePlaying ? styles.demoControlActive : ''}
                  disabled={liveDone}
                  onClick={() => {
                    setLivePlaying(!livePlaying);
                    setIsOpen(true);
                  }}>
                  {livePlaying ? 'Pause live hand' : liveDone ? 'Live hand done' : 'Play live hand'}
                </button>
                <button
                  type='button'
                  onClick={() => {
                    restartLiveHand();
                    setIsOpen(true);
                  }}>
                  Restart
                </button>
              </div>
              <p className={styles.demoNote}>
                Play adds a row to the live hand every 2 seconds. Scroll the list down first to
                watch the completed hands hold still.
              </p>
            </>
          )}
          {!demoLocked && (
            <label className={styles.demoScenario}>
              Completed-hand example
              <select
                value={selectedIndex}
                onChange={(event) => {
                  setSelectedIndex(Number(event.target.value));
                  setIsOpen(true);
                }}>
                {data.completedHands.map((hand, index) => (
                  <option key={hand.handId} value={index}>
                    {handHistoryScenarioLabels[hand.handId]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
    </div>
  );
};
